import { useAuthStore } from '@/auth/authStore';
import { SERVER_URL, COMMON_HEADERS } from '@/config/serverUrl';
import type { RoomDto, RoomMode, RoomInvitePayload } from '@shared/protocol';
import type { TeamMode } from '@shared/types';

const BASE = SERVER_URL;

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...COMMON_HEADERS,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body == null ? undefined : JSON.stringify(body),
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = '';
    try {
      const j = await res.json();
      detail = j?.message ?? j?.error ?? '';
    } catch { /* noop */ }
    throw new Error(detail || `Room API ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const roomApi = {
  mine: () => req<{ room: RoomDto | null }>('GET', '/rooms/mine'),
  byCode: (code: string) => req<{ room: RoomDto }>('GET', `/rooms/by-code/${code}`),
  create: () => req<{ room: RoomDto }>('POST', '/rooms/create', {}),
  join: (code: string, slot?: 1 | 2 | 3 | 4) =>
    req<{ room: RoomDto }>('POST', `/rooms/by-code/${code}/join`, { slot }),
  move: (code: string, slot: 1 | 2 | 3 | 4) =>
    req<{ room: RoomDto }>('POST', `/rooms/by-code/${code}/move`, { slot }),
  setReady: (code: string, ready: boolean) =>
    req<{ room: RoomDto }>('POST', `/rooms/by-code/${code}/ready`, { ready }),
  setTeamMode: (code: string, teamMode: TeamMode) =>
    req<{ room: RoomDto }>('POST', `/rooms/by-code/${code}/team-mode`, { teamMode }),
  kick: (code: string, userId: string) =>
    req<{ room: RoomDto }>('POST', `/rooms/by-code/${code}/kick`, { userId }),
  leave: (code: string) =>
    req<{ ok: true }>('POST', `/rooms/by-code/${code}/leave`),
  start: (code: string) =>
    req<{ ok: true; mode: RoomMode; matchId?: string; queuedUserIds?: string[]; teamMode?: TeamMode }>(
      'POST',
      `/rooms/by-code/${code}/start`,
    ),
  invite: (code: string, userId: string) =>
    req<{ ok: true; expiresAt: number }>('POST', `/rooms/by-code/${code}/invite`, { userId }),
  myInvites: () =>
    req<{ invites: RoomInvitePayload[] }>('GET', '/rooms/invites/mine'),
  acceptInvite: (code: string) =>
    req<{ room: RoomDto }>('POST', `/rooms/invites/${code}/accept`),
  declineInvite: (code: string) =>
    req<{ ok: true }>('POST', `/rooms/invites/${code}/decline`),
};
